import { useTranslation } from 'react-i18next';
import { IDIOMAS_DISPONIBLES, guardarIdioma, type Idioma } from '../../i18n/configuracion';
import { useSesion } from '../../sesion/ProveedorDeSesion';
import { TEMAS, aplicarTema, temaGuardado, type Tema } from '../../tema/tema';

import { MarcaAdaptable } from '../Marca';
import { IrA } from './IrA';
import { Bandera } from './Bandera';
import { MenuDeUsuario } from './MenuDeUsuario';

const NOMBRES_DE_IDIOMA: Record<Idioma, string> = {
  'es-AR': 'Español',
  en: 'English',
  'pt-BR': 'Português',
};

const CLASES_DE_SELECTOR = 'rounded-marco border border-borde bg-transparent px-2 py-1 text-xs';

/**
 * Barra superior: marca, empresa activa y las preferencias de quien opera.
 *
 * El selector de empresa sólo aparece si hay más de una. Un desplegable con una sola
 * opción ocupa lugar y no decide nada.
 */
export function BarraSuperior({
  sinComercio = false,
}: {
  /**
   * Sin comercio activo —quien administra la plataforma y no está parado en ninguno—. Cambian
   * dos cosas: **el logotipo va acá**, porque no hay menú que lo lleve, y **no hay «Ir a…»**,
   * porque busca pantallas de un comercio y acá no hay comercio.
   */
  readonly sinComercio?: boolean;
} = {}): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { perfil, empresaActivaId, pararseEn } = useSesion();

  const empresas = (perfil?.empresas ?? []).filter((empresa) => empresa.activa);
  const actual = empresas.find((empresa) => empresa.empresaId === empresaActivaId);

  return (
    // El filo rojo debajo de la barra es toda la presencia de marca que necesita una pantalla de
    // trabajo: se ve siempre, no compite con ningún control, y no pinta de alarma la jornada.
    <header className="border-b-2 border-marca bg-superficie">
      <div className="flex items-center justify-between gap-4 px-4 py-2">
        <div className="flex items-center gap-3">
          {/* **El logotipo ya está en el menú.** Repetirlo acá lo gastaba: dos veces la misma
              marca en la misma pantalla deja de leerse como marca y pasa a ser ruido. La barra
              superior es del comercio —su nombre es lo que cambia al pararse en otra empresa— y por
              eso el nombre pasó a ser el título y no una aclaración gris al costado. */}
          {sinComercio ? (
            <MarcaAdaptable forma="logotipo" className="h-5 w-auto" />
          ) : empresas.length > 1 ? (
            <label className="flex items-center gap-1.5">
              <span className="sr-only">{t('navegacion.empresaActiva')}</span>
              <select
                value={empresaActivaId ?? ''}
                onChange={(evento) => void pararseEn(evento.target.value)}
                className={CLASES_DE_SELECTOR}
              >
                {empresas.map((empresa) => (
                  <option key={empresa.empresaId} value={empresa.empresaId}>
                    {empresa.nombre}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <span className="text-cuerpo font-medium">{actual?.nombre ?? ''}</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Antes que los selectores de preferencia: se usa todos los días, ellos casi nunca. */}
          {sinComercio ? null : <IrA />}
          {/* Los idiomas como botones con su bandera y no como desplegable: son tres, entran, y
              un `<select>` no puede dibujar una bandera adentro de sus opciones. */}
          <div
            className="flex items-center gap-0.5 rounded-marco border border-borde p-0.5"
            role="group"
            aria-label={t('idioma.titulo')}
          >
            {IDIOMAS_DISPONIBLES.map((idioma) => {
              const actual =
                IDIOMAS_DISPONIBLES.find((posible) => posible === i18n.language) ?? 'es-AR';
              return (
                <button
                  key={idioma}
                  type="button"
                  aria-pressed={idioma === actual}
                  title={NOMBRES_DE_IDIOMA[idioma]}
                  onClick={() => {
                    void i18n.changeLanguage(idioma);
                    guardarIdioma(idioma);
                  }}
                  className={`flex items-center gap-1 rounded-[0.35rem] px-1.5 py-1 text-apoyo transition-colors ${
                    idioma === actual
                      ? 'bg-acento-suave font-medium text-acento'
                      : 'text-texto-tenue hover:bg-acento-suave'
                  }`}
                >
                  <Bandera idioma={idioma} />
                  <span className="sr-only">{NOMBRES_DE_IDIOMA[idioma]}</span>
                </button>
              );
            })}
          </div>

          <label className="flex items-center gap-1.5">
            <span className="sr-only">{t('tema.titulo')}</span>
            <select
              defaultValue={temaGuardado()}
              onChange={(evento) => {
                aplicarTema(evento.target.value as Tema);
              }}
              className={CLASES_DE_SELECTOR}
            >
              {TEMAS.map((tema) => (
                <option key={tema} value={tema}>
                  {t(`tema.${tema}`)}
                </option>
              ))}
            </select>
          </label>

          <MenuDeUsuario />
        </div>
      </div>
    </header>
  );
}
